/**
 * Where a task opens.
 *
 * The app keeps two working lists for tasks and they are split on one field:
 * Tasks (`/tasks`) drops every task carrying equipment, and the maintenance
 * Schedule (`/maintain/schedule`) drops every task without one. So a link that
 * ignores `equipment` lands the reader on a list its record cannot appear in —
 * which is what `/maintenance?task-id=`, the pre-Maintain standalone route, did
 * for every kind alike.
 *
 * Both lists honour `?task-id=` by mounting SingleTaskModal, which fetches the
 * task by id — the record opens even when the list's own filters exclude it.
 */

export const TASK_ID_SEARCH_PARAM = 'task-id';

const TASKS_ROUTE = '/tasks';
const SCHEDULE_ROUTE = '/maintain/schedule';

/**
 * The minimum a caller must know to route a task: its id, and whether it names
 * equipment. Search hits, notification payloads and full task responses all
 * satisfy it.
 */
export type RoutableTask = {
  id: string;
  equipment?: { id: string; name?: string } | null;
};

/** 'maintenance' is a maintenance job or a service request — both carry equipment. */
export type TaskKind = 'maintenance' | 'task';

export const taskKind = (task: RoutableTask): TaskKind => (task.equipment ? 'maintenance' : 'task');

/** The list that owns the task, with the task itself opened on top of it. */
export const taskHref = (task: RoutableTask): string => {
  const route = taskKind(task) === 'maintenance' ? SCHEDULE_ROUTE : TASKS_ROUTE;
  return `${route}?${TASK_ID_SEARCH_PARAM}=${task.id}`;
};
